import { useId, useState } from 'react';
import type { FormEvent } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useDiscardGuard } from '@/shared/hooks/useDiscardGuard';

import type { AiProvider, AiProviderType } from '../types';
import { validateBaseUrl, validateName } from '../validation';
import { isAiProviderError } from '../errors';
import type { AiProviderErrorField } from '../errors';
import { AiProviderTestConnectionButton } from './AiProviderTestConnectionButton';

/** The org's Claude connection runs through this gateway, not api.anthropic.com directly. */
const DEFAULT_BASE_URL = 'https://anthropic.generative.engine.capgemini.com';

export type AiProviderFormMode =
  { kind: 'create' } | { kind: 'edit'; entry: AiProvider };

export interface AiProviderFormValues {
  name: string;
  provider_type: AiProviderType;
  base_url: string;
  /** `undefined` means "leave unchanged" on edit (spec FR-007); required on create. */
  token?: string;
}

/**
 * Add an AI Provider, or edit an existing one.
 *
 * Mirrors `llm-profiles/components/LlmProfileFormSheet.tsx`'s load-bearing
 * decisions: remount per target, dismissal blocked while a save is in
 * flight, a failed submit keeps the sheet open with the input intact.
 *
 * The token field never shows a previously-saved value (spec FR-006) — on
 * edit it starts blank with placeholder guidance, and submitting it blank
 * means "keep the stored token" (FR-007). On create it is required.
 *
 * No "Provider type" field: this sheet is only ever opened from the Claude
 * group (spec FR-002a — no other `provider_type` is creatable yet), so the
 * type is fixed to `anthropic` rather than exposed as a choice with one
 * enabled option.
 */
export function AiProviderFormSheet({
  mode,
  open,
  isPending,
  error,
  onSubmit,
  onClearError,
  onCancel,
}: {
  mode: AiProviderFormMode;
  open: boolean;
  isPending: boolean;
  error: unknown;
  onSubmit: (input: AiProviderFormValues) => void;
  onClearError: () => void;
  onCancel: () => void;
}) {
  const editing = mode.kind === 'edit';
  const entry = editing ? mode.entry : undefined;

  const providerType: AiProviderType = entry?.provider_type ?? 'anthropic';

  const [name, setName] = useState(entry?.name ?? '');
  const [baseUrl, setBaseUrl] = useState(entry?.base_url ?? DEFAULT_BASE_URL);
  const [token, setToken] = useState('');
  const [tokenVisible, setTokenVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const nameId = useId();
  const nameErrorId = useId();
  const baseUrlId = useId();
  const baseUrlErrorId = useId();
  const tokenId = useId();
  const tokenErrorId = useId();

  const serverError = isAiProviderError(error) ? error : undefined;
  const genericError =
    error && !serverError ? 'The request failed. Please try again.' : undefined;

  const localNameError = validateName(name);
  const localBaseUrlError = validateBaseUrl(baseUrl);
  const localTokenError =
    !editing && token.trim() === '' ? 'Enter a token.' : null;

  function messageFor(field: AiProviderErrorField): string | undefined {
    if (serverError?.field === field) return serverError.message;
    if (field === 'form') return genericError;
    if (field === 'name')
      return submitted ? (localNameError ?? undefined) : undefined;
    if (field === 'base_url')
      return submitted ? (localBaseUrlError ?? undefined) : undefined;
    if (field === 'token')
      return submitted ? (localTokenError ?? undefined) : undefined;
    return undefined;
  }

  const canSubmit = name.trim().length > 0 && !isPending;
  // Fields "populated" means whatever the connection needs to actually work:
  // a name and a base URL always, plus a token — on create because nothing
  // is saved yet to test without one, on edit because typing a fresh one is
  // itself a reason to want to try the connection right away, even if
  // something else on the form is momentarily incomplete.
  const canTest =
    (name.trim().length > 0 && baseUrl.trim().length > 0) ||
    token.trim().length > 0;

  // What Test should actually check. The rule above deliberately enables the
  // button the moment a fresh token is typed — "typing a fresh one is itself a
  // reason to want to try the connection right away" — but the check used to run
  // against the *stored* pair, so it answered a question nobody asked and a
  // stale token looked like a rejection of the new one. Sending the edited
  // fields makes the button test what the admin is looking at.
  //
  // `undefined` when nothing was edited, so an untouched form keeps testing the
  // saved provider — and keeps persisting that verdict to the badge, which an
  // overridden check deliberately does not do.
  const editedBaseUrl = baseUrl.trim() !== (entry?.base_url ?? '').trim();
  const editedToken = token.trim().length > 0;
  const pendingCredentials =
    editedBaseUrl || editedToken
      ? {
          ...(editedBaseUrl ? { base_url: baseUrl.trim() } : {}),
          ...(editedToken ? { token: token.trim() } : {}),
        }
      : undefined;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;
    // The mirrored rules inform; a submit attempt that disagrees with one
    // (base URL shape, a missing token on create) never reaches the server —
    // same "inform, don't gate the button" split `LlmProfileFormSheet` uses,
    // so the message is always reachable by clicking Add rather than sitting
    // behind a disabled button no click can trigger.
    if (localBaseUrlError !== null || localTokenError !== null) return;
    onSubmit({
      name,
      provider_type: providerType,
      base_url: baseUrl.trim(),
      token: token.trim() === '' ? undefined : token.trim(),
    });
  }

  // The token never leaves this component; only whether one was typed counts.
  const dirty =
    name !== (entry?.name ?? '') ||
    baseUrl !== (entry?.base_url ?? DEFAULT_BASE_URL) ||
    token !== '';
  // A successful save closes through `open`, so it never asks.
  const { requestClose, dialog } = useDiscardGuard(dirty, onCancel);

  const nameMessage = messageFor('name');
  const baseUrlMessage = messageFor('base_url');
  const tokenMessage = messageFor('token');
  const formMessage = messageFor('form');

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (isPending) return;
          if (!next) requestClose();
        }}
      >
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {editing ? `Edit ${entry?.name}` : 'Add a Claude connection'}
            </SheetTitle>
            <SheetDescription>
              An AI Provider owns the base URL and credential a workflow reaches
              the model through.
            </SheetDescription>
          </SheetHeader>

          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <SheetBody className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={nameId}>Connection name</Label>
                  <RequiredMark />
                </div>
                <Input
                  id={nameId}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    if (serverError?.field === 'name') onClearError();
                  }}
                  disabled={isPending}
                  placeholder="Global Claude"
                  autoFocus
                  aria-invalid={nameMessage ? true : undefined}
                  aria-describedby={nameMessage ? nameErrorId : undefined}
                />
                {nameMessage ? (
                  <p id={nameErrorId} className="text-destructive text-xs">
                    {nameMessage}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={baseUrlId}>Base URL</Label>
                  <RequiredMark />
                </div>
                <Input
                  id={baseUrlId}
                  value={baseUrl}
                  onChange={(event) => {
                    setBaseUrl(event.target.value);
                    if (serverError?.field === 'base_url') onClearError();
                  }}
                  disabled={isPending}
                  placeholder="https://api.anthropic.com"
                  className="font-mono"
                  aria-invalid={baseUrlMessage ? true : undefined}
                  aria-describedby={baseUrlMessage ? baseUrlErrorId : undefined}
                />
                {baseUrlMessage ? (
                  <p id={baseUrlErrorId} className="text-destructive text-xs">
                    {baseUrlMessage}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={tokenId}>API Token</Label>
                  {editing ? null : <RequiredMark />}
                </div>
                <div className="flex gap-1.5">
                  <Input
                    id={tokenId}
                    type={tokenVisible ? 'text' : 'password'}
                    value={token}
                    onChange={(event) => {
                      setToken(event.target.value);
                      if (serverError?.field === 'token') onClearError();
                    }}
                    disabled={isPending}
                    placeholder={
                      editing
                        ? '•••• leave blank to keep unchanged'
                        : 'sk-ant-…'
                    }
                    autoComplete="off"
                    aria-invalid={tokenMessage ? true : undefined}
                    aria-describedby={tokenMessage ? tokenErrorId : undefined}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isPending}
                    onClick={() => {
                      setTokenVisible((visible) => !visible);
                    }}
                  >
                    {tokenVisible ? (
                      <EyeOffIcon aria-hidden="true" />
                    ) : (
                      <EyeIcon aria-hidden="true" />
                    )}
                    {tokenVisible ? 'Hide' : 'Show'}
                  </Button>
                </div>
                {tokenMessage ? (
                  <p id={tokenErrorId} className="text-destructive text-xs">
                    {tokenMessage}
                  </p>
                ) : null}
              </div>

              {mode.kind === 'edit' ? (
                <AiProviderTestConnectionButton
                  id={mode.entry.id}
                  overrides={pendingCredentials}
                  disabled={!canTest}
                />
              ) : (
                // Nothing exists to test yet. Editing can check unsaved values
                // (they ride along as overrides), but the endpoint is addressed by
                // provider id — there is no id before the first save, so on create
                // this stays disabled until the connection is saved.
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled
                  title="Save the connection first"
                  className="self-start"
                >
                  Test connection
                </Button>
              )}

              {formMessage ? (
                <p role="alert" className="text-destructive text-sm">
                  {formMessage}
                </p>
              ) : null}
            </SheetBody>

            <SheetFooter>
              <SheetClose
                render={<Button type="button" variant="outline" />}
                disabled={isPending}
              >
                Cancel
              </SheetClose>
              <Button type="submit" disabled={!canSubmit}>
                {isPending ? 'Saving…' : editing ? 'Save' : 'Add'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {dialog}
    </>
  );
}
